import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Tab } from '@headlessui/react'
import {
  EnvelopeIcon,
  UserGroupIcon,
  CalendarIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  PencilIcon,
  TrashIcon,
  ChatBubbleLeftIcon,
  ArrowLeftIcon,
  LinkIcon,
  CheckIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'
import { boxApi, invitationApi } from '../utils/api'
import { useAuth } from '../context/AuthContext'
import InviteMembers from '../components/InviteMembers'
import MemberList from '../components/MemberList'
import PayoutSchedule from '../components/PayoutSchedule'
import Ledger from '../components/Ledger'
import AuditLog from '../components/AuditLog'
import EditBoxModal from '../components/EditBoxModal'
import DeleteBoxModal from '../components/DeleteBoxModal'
import ScheduleBuilder from '../components/ScheduleBuilder'
import MessageWall from '../components/MessageWall'
import BoxAdvisor from '../components/BoxAdvisor'

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export default function BoxDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { currentUser } = useAuth()
  const [box, setBox] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [invitations, setInvitations] = useState([])
  const [invitesLoading, setInvitesLoading] = useState(false)
  const [invitesError, setInvitesError] = useState(null)
  const [inviteAction, setInviteAction] = useState({ id: null, type: null })
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [scheduleVersion, setScheduleVersion] = useState(0)

  useEffect(() => {
    const loadBox = async () => {
      try {
        setLoading(true)
        const data = await boxApi.getById(id)
        setBox(data)
      } catch (err) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }
    loadBox()
  }, [id])

  const loadInvitations = useCallback(async () => {
    try {
      setInvitesLoading(true)
      setInvitesError(null)
      const data = await boxApi.getInvitations(id)
      setInvitations(data)
    } catch (err) {
      setInvitesError(err.message)
    } finally {
      setInvitesLoading(false)
    }
  }, [id])

  useEffect(() => {
    loadInvitations()
  }, [loadInvitations])

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading box details...</div>
      </div>
    )
  }

  if (error || !box) {
    return (
      <div className="bg-red-50 p-4 rounded-md">
        <p className="text-red-800">Error loading box: {error || 'Box not found'}</p>
        <button
          onClick={() => navigate('/boxes')}
          className="mt-2 text-primary-600 hover:text-primary-700"
        >
          Back to boxes
        </button>
      </div>
    )
  }

  const handleEditBox = async (data) => {
    await boxApi.update(id, data)
    // Reload box data
    const updatedBox = await boxApi.getById(id)
    setBox(updatedBox)
  }

  const handleDeleteBox = async (reason) => {
    await boxApi.delete(id, { reason })
    // Navigate back to boxes list after deletion
    navigate('/boxes')
  }

  const handleScheduleSaved = async () => {
    setScheduleVersion((prev) => prev + 1)
    try {
      const refreshed = await boxApi.getById(id)
      setBox(refreshed)
    } catch (_err) {
      // Best-effort refresh; ignore failures
    }
  }

  const handleResendInvitation = async (invitationId) => {
    try {
      setInviteAction({ id: invitationId, type: 'resend' })
      setInvitesError(null)
      await invitationApi.resend(invitationId)
      await loadInvitations()
    } catch (err) {
      setInvitesError(err.message)
    } finally {
      setInviteAction({ id: null, type: null })
    }
  }

  const handleDeleteInvitation = async (invitationId) => {
    try {
      setInviteAction({ id: invitationId, type: 'delete' })
      setInvitesError(null)
      await invitationApi.remove(invitationId)
      await loadInvitations()
    } catch (err) {
      setInvitesError(err.message)
    } finally {
      setInviteAction({ id: null, type: null })
    }
  }
  const isOrganizer = box.organizer_id === currentUser?.uid

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mb-4">
        <Link
          to="/boxes"
          className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to boxes
        </Link>
      </div>
      {/* Header */}
      <div className="mb-8 bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-4">
              <div className="h-12 w-12 rounded-xl bg-primary-100 flex items-center justify-center text-primary-600 font-bold text-xl">
                {box.name.charAt(0)}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{box.name}</h1>
                <p className="text-sm text-gray-500 mt-1">{box.description}</p>
              </div>
            </div>
          </div>
          <div className="mt-4 sm:mt-0 flex items-center space-x-3">
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${
                box.status === 'active'
                  ? 'bg-green-50 text-green-700 border-green-100'
                  : box.status === 'pending'
                  ? 'bg-yellow-50 text-yellow-700 border-yellow-100'
                  : 'bg-gray-50 text-gray-700 border-gray-100'
              }`}
            >
              <span className={`h-2 w-2 rounded-full mr-2 ${
                box.status === 'active' ? 'bg-green-500' : box.status === 'pending' ? 'bg-yellow-500' : 'bg-gray-500'
              }`}></span>
              {box.status.charAt(0).toUpperCase() + box.status.slice(1)}
            </span>
            {isOrganizer && (
              <>
                <button
                  onClick={() => setShowEditModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-full shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <PencilIcon className="h-4 w-4 mr-1.5" />
                  Edit
                </button>
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-red-300 rounded-full shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <TrashIcon className="h-4 w-4 mr-1.5" />
                  Delete
                </button>
              </>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-5 border-t border-gray-100 pt-8">
          <div className="text-center sm:text-left">
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Members</dt>
            <dd className="mt-1 text-2xl font-semibold text-gray-900">{box.member_count ?? 0}</dd>
          </div>
          <div className="text-center sm:text-left border-l border-gray-100 pl-4 sm:pl-8">
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</dt>
            <dd className="mt-1 text-2xl font-semibold text-gray-900">{box.cycle_duration_months} <span className="text-sm font-normal text-gray-400">mo</span></dd>
          </div>
          <div className="text-center sm:text-left border-l border-gray-100 pl-4 sm:pl-8">
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Contribution</dt>
            <dd className="mt-1 text-2xl font-semibold text-gray-900">${box.contribution_amount}</dd>
          </div>
          <div className="text-center sm:text-left border-l border-gray-100 pl-4 sm:pl-8">
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Payouts / mo</dt>
            <dd className="mt-1 text-2xl font-semibold text-gray-900">{box.payouts_per_month || 1}</dd>
          </div>
          <div className="text-center sm:text-left border-l border-gray-100 pl-4 sm:pl-8">
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</dt>
            <dd className="mt-1 text-lg font-semibold text-gray-900">
              {box.start_date ? new Date(box.start_date).toLocaleDateString() : 'Not set'}
            </dd>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tab.Group>
        <Tab.List className="flex flex-nowrap gap-2 rounded-xl bg-gray-100/50 p-1.5 mb-8 overflow-x-auto sm:flex-wrap sm:overflow-visible">
          <Tab
            className={({ selected }) =>
              classNames(
                'flex-none sm:flex-1 min-w-[140px] rounded-lg py-2.5 text-sm font-medium leading-5 transition-all duration-200',
                'focus:outline-none focus:ring-2 ring-offset-2 ring-offset-gray-50 ring-primary-400',
                selected
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
              )
            }
          >
            <div className="flex items-center justify-center">
              <UserGroupIcon className="h-5 w-5 mr-2" />
              Members
            </div>
          </Tab>
          <Tab
            className={({ selected }) =>
              classNames(
                'flex-none sm:flex-1 min-w-[140px] rounded-lg py-2.5 text-sm font-medium leading-5 transition-all duration-200',
                'focus:outline-none focus:ring-2 ring-offset-2 ring-offset-gray-50 ring-primary-400',
                selected
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
              )
            }
          >
            <div className="flex items-center justify-center">
              <CalendarIcon className="h-5 w-5 mr-2" />
              Schedule
            </div>
          </Tab>
          <Tab
            className={({ selected }) =>
              classNames(
                'flex-none sm:flex-1 min-w-[140px] rounded-lg py-2.5 text-sm font-medium leading-5 transition-all duration-200',
                'focus:outline-none focus:ring-2 ring-offset-2 ring-offset-gray-50 ring-primary-400',
                selected
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
              )
            }
          >
            <div className="flex items-center justify-center">
              <DocumentTextIcon className="h-5 w-5 mr-2" />
              Ledger
            </div>
          </Tab>
          <Tab
            className={({ selected }) =>
              classNames(
                'flex-none sm:flex-1 min-w-[140px] rounded-lg py-2.5 text-sm font-medium leading-5 transition-all duration-200',
                'focus:outline-none focus:ring-2 ring-offset-2 ring-offset-gray-50 ring-primary-400',
                selected
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
              )
            }
          >
            <div className="flex items-center justify-center">
              <ChatBubbleLeftIcon className="h-5 w-5 mr-2" />
              Messages
            </div>
          </Tab>
          {isOrganizer && (
            <Tab
              className={({ selected }) =>
                classNames(
                  'flex-none sm:flex-1 min-w-[140px] rounded-lg py-2.5 text-sm font-medium leading-5 transition-all duration-200',
                  'focus:outline-none focus:ring-2 ring-offset-2 ring-offset-gray-50 ring-primary-400',
                  selected
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
                )
              }
            >
              <div className="flex items-center justify-center">
                <SparklesIcon className="h-5 w-5 mr-2" />
                Advisor
              </div>
            </Tab>
          )}
          {isOrganizer && (
            <Tab
              className={({ selected }) =>
                classNames(
                  'flex-none sm:flex-1 min-w-[140px] rounded-lg py-2.5 text-sm font-medium leading-5 transition-all duration-200',
                  'focus:outline-none focus:ring-2 ring-offset-2 ring-offset-gray-50 ring-primary-400',
                  selected
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
                )
              }
            >
              <div className="flex items-center justify-center">
                <EnvelopeIcon className="h-5 w-5 mr-2" />
                Invite
              </div>
            </Tab>
          )}
          {isOrganizer && (
            <Tab
              className={({ selected }) =>
                classNames(
                  'flex-none sm:flex-1 min-w-[140px] rounded-lg py-2.5 text-sm font-medium leading-5 transition-all duration-200',
                  'focus:outline-none focus:ring-2 ring-offset-2 ring-offset-gray-50 ring-primary-400',
                  selected
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
                )
              }
            >
              <div className="flex items-center justify-center">
                <ShieldCheckIcon className="h-5 w-5 mr-2" />
                Audit
              </div>
            </Tab>
          )}
        </Tab.List>
        <Tab.Panels>
          <Tab.Panel className="focus:outline-none">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <MemberList boxId={id} canManage={isOrganizer} organizerId={box.organizer_id} />
            </div>
          </Tab.Panel>
          <Tab.Panel className="focus:outline-none">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              {isOrganizer && <ScheduleBuilder boxId={id} box={box} onSaved={handleScheduleSaved} />}
              <PayoutSchedule boxId={id} refreshKey={scheduleVersion} isOrganizer={isOrganizer} />
            </div>
          </Tab.Panel>
          <Tab.Panel className="focus:outline-none">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <Ledger boxId={id} canManage={isOrganizer} />
            </div>
          </Tab.Panel>
          <Tab.Panel className="focus:outline-none">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden p-6">
              <MessageWall boxId={id} canManage={isOrganizer} />
            </div>
          </Tab.Panel>
          {isOrganizer && (
            <Tab.Panel className="focus:outline-none">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <BoxAdvisor boxId={id} />
              </div>
            </Tab.Panel>
          )}
          {isOrganizer && (
            <Tab.Panel className="focus:outline-none">
              <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden p-6">
                  <InviteMembers boxId={id} boxName={box.name} onInviteSent={loadInvitations} />
                </div>
                <PendingInvitesSection
                  invitations={invitations}
                  loading={invitesLoading}
                  error={invitesError}
                  onResend={handleResendInvitation}
                  onDelete={handleDeleteInvitation}
                  actionState={inviteAction}
                />
              </div>
            </Tab.Panel>
          )}
          {isOrganizer && (
            <Tab.Panel className="focus:outline-none">
              <AuditLog boxId={id} />
            </Tab.Panel>
          )}
        </Tab.Panels>
      </Tab.Group>

      {/* Modals */}
      {isOrganizer && (
        <>
          <EditBoxModal
            isOpen={showEditModal}
            onClose={() => setShowEditModal(false)}
            box={box}
            onSave={handleEditBox}
          />
          <DeleteBoxModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            box={box}
            onDelete={handleDeleteBox}
          />
        </>
      )}
    </div>
  )
}

function PendingInvitesSection({ invitations, loading, error, onResend, onDelete, actionState }) {
  const pendingInvites = invitations.filter((invite) => invite.status === 'pending')
  const [copiedInviteId, setCopiedInviteId] = useState(null)

  const handleCopyLink = async (inviteLink, inviteId) => {
    try {
      await navigator.clipboard.writeText(inviteLink)
      setCopiedInviteId(inviteId)
      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopiedInviteId(null)
      }, 2000)
    } catch (err) {
      console.error('Failed to copy link:', err)
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = inviteLink
      textArea.style.position = 'fixed'
      textArea.style.opacity = '0'
      document.body.appendChild(textArea)
      textArea.select()
      try {
        document.execCommand('copy')
        setCopiedInviteId(inviteId)
        setTimeout(() => {
          setCopiedInviteId(null)
        }, 2000)
      } catch (fallbackErr) {
        console.error('Fallback copy failed:', fallbackErr)
      }
      document.body.removeChild(textArea)
    }
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Pending invitations</h3>
            <p className="text-sm text-gray-500">
              Resend or remove invites that haven&apos;t been accepted yet. Copy the invite link to share manually if needed.
            </p>
          </div>
        </div>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      </div>

      {loading ? (
        <div className="p-8 text-center text-gray-500">Loading invitations...</div>
      ) : pendingInvites.length === 0 ? (
        <div className="p-8 text-center text-gray-500">No pending invitations.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invited
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pendingInvites.map((invite) => {
                const isResending = actionState.id === invite.id && actionState.type === 'resend'
                const isDeleting = actionState.id === invite.id && actionState.type === 'delete'
                const isCopied = copiedInviteId === invite.id
                return (
                  <tr key={invite.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{invite.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {invite.name || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invite.created_at ? new Date(invite.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-3">
                        {invite.invite_link && (
                          <button
                            onClick={() => handleCopyLink(invite.invite_link, invite.id)}
                            disabled={isResending || isDeleting}
                            className="inline-flex items-center text-gray-600 hover:text-primary-600 disabled:opacity-50 transition-colors"
                            title="Copy invite link"
                          >
                            {isCopied ? (
                              <>
                                <CheckIcon className="h-4 w-4 mr-1" />
                                <span>Copied!</span>
                              </>
                            ) : (
                              <>
                                <LinkIcon className="h-4 w-4 mr-1" />
                                <span>Copy link</span>
                              </>
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => onResend(invite.id)}
                          disabled={isResending || isDeleting}
                          className="text-primary-600 hover:text-primary-800 disabled:opacity-50"
                        >
                          {isResending ? 'Resending…' : 'Resend'}
                        </button>
                        <button
                          onClick={() => onDelete(invite.id)}
                          disabled={isResending || isDeleting}
                          className="text-red-500 hover:text-red-600 disabled:opacity-50"
                        >
                          {isDeleting ? 'Deleting…' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
